<template>
  <!-- Two thirds of the Overview grid with the number behind the advice beside it
       (design C); the card takes the whole row when there is no number to show -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <section class="rounded-lg bg-gray-900 px-6 flex flex-col" :class="showLead ? 'lg:col-span-2' : 'lg:col-span-3'">
      <div class="flex flex-wrap items-center gap-3 pt-5 pb-4">
        <div class="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center shrink-0">
          <img class="w-5 h-5" src="../../../assets/icons/coach.svg" alt="" />
        </div>
        <div class="flex flex-col items-start gap-1">
          <h2 class="text-text-primary text-sm font-medium">{{ $t('coach.page.title') }}</h2>
          <Pill class="px-1 py-[1px]" :text="$t('coach.feedback_card.ai_coach')" bgColor="#8A8EDB20" textColor="#8A8EDB" />
        </div>
        <div class="flex-1" />
        <Pill
          v-if="state === 'locked'"
          class="px-1 py-[1px]"
          :text="$t('coach.feedback_card.competitor_tier')"
          bgColor="#EF6B6B20"
          textColor="#EF6B6B"
        />
        <div
          v-else
          role="group"
          :aria-label="$t('coach.profile_feedback_card.period_label')"
          class="flex p-0.5 bg-gray-800 border border-gray-700 rounded-md"
        >
          <button
            type="button"
            v-for="days in PROFILE_PERIODS"
            :key="days"
            :aria-pressed="period === days"
            class="h-7 px-3 rounded text-xs duration-300"
            :class="period === days ? 'bg-gray-700 text-text-primary font-medium' : 'text-text-secondary hover:text-text-primary'"
            @click="period = days"
          >
            {{ $t('coach.profile_feedback_card.period_days', { days }) }}
          </button>
        </div>
        <Pill class="px-1 py-[1px]" :text="$t('coach.feedback_card.beta')" bgColor="#D071C520" textColor="#D071C5" />
        <Info
          :title="$t('coach.feedback_card.info_title')"
          :description="$t('coach.profile_feedback_card.info_description')"
        />
      </div>
      <div class="h-px bg-gray-800" />

      <div class="flex flex-col gap-4 pt-5 pb-6">
        <div class="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
          <span>{{ mapText }}</span>
          <span aria-hidden="true">·</span>
          <span>{{ $t('coach.profile_feedback_card.since', { date: sinceText }) }}</span>
          <template v-if="state === 'ready' && updatedText">
            <span aria-hidden="true">·</span>
            <span>{{ $t('coach.profile_feedback_card.updated', { ago: updatedText }) }}</span>
          </template>
        </div>

        <template v-if="state === 'ready'">
          <!-- D2: each reason sits on the phrase it explains -->
          <template v-if="annotated.annotated">
            <AnnotatedAdvice class="text-text-primary text-lg leading-[30px]" :segments="annotated.segments" />
            <p class="text-gray-400 text-xs">{{ $t('coach.profile_feedback_card.annotated_hint') }}</p>
          </template>
          <!-- Not annotated (design A): advice, its reasons beside it behind a thin rule -->
          <div
            v-else
            class="grid grid-cols-1 gap-6"
            :class="split.reasoning ? 'lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)]' : ''"
          >
            <p class="text-text-primary text-base leading-6 whitespace-pre-line">{{ split.advice }}</p>
            <template v-if="split.reasoning">
              <div class="hidden lg:block bg-gray-800" aria-hidden="true" />
              <div class="flex flex-col gap-2">
                <h3 class="text-xs font-medium uppercase tracking-[1.5px] text-gray-400">{{ $t('coach.annotated_advice.why') }}</h3>
                <p class="text-text-secondary text-sm leading-[21px] whitespace-pre-line">{{ split.reasoning }}</p>
              </div>
            </template>
          </div>
          <p v-if="narrowedByOtherFilters" class="text-text-secondary text-xs">
            {{ $t('coach.profile_feedback_card.all_platforms_note') }}
          </p>
          <FeedbackThumbs
            :key="result.generation_id || result.generated_at"
            :rating="result.rating ?? null"
            url="/coach/profile_feedback/rating"
            :params="filterParams()"
          />
        </template>

        <template v-else-if="state === 'locked'">
          <p class="text-text-primary text-base leading-6 blur-[5px] select-none" aria-hidden="true">
            {{ $t('coach.profile_feedback_card.locked_placeholder') }}
          </p>
          <p class="text-text-secondary text-sm">{{ $t('coach.profile_feedback_card.locked') }}</p>
          <div class="flex flex-wrap items-center gap-3">
            <router-link
              to="/upgrade"
              class="py-2.5 px-3 rounded-md bg-primary-600 text-text-primary text-xs font-medium hover:bg-primary-500 duration-300"
            >
              {{ $t('coach.feedback_card.upgrade_to_read') }}
            </router-link>
            <span class="text-text-secondary text-xs">{{ $t('coach.feedback_card.upgrade_caption_free') }}</span>
          </div>
        </template>

        <template v-else-if="state === 'writing' || state === 'loading'">
          <div class="flex flex-col gap-2.5 animate-pulse" aria-hidden="true">
            <div class="h-3.5 rounded bg-gray-800 w-full" />
            <div class="h-3.5 rounded bg-gray-800 w-[92%]" />
            <div class="h-3.5 rounded bg-gray-800 w-[46%]" />
          </div>
          <p v-if="state === 'writing'" class="text-text-secondary text-sm">{{ $t('coach.profile_feedback_card.writing') }}</p>
        </template>

        <template v-else-if="state === 'idle'">
          <p class="text-text-secondary text-sm">
            {{ $t('coach.profile_feedback_card.idle', { map: mapText, days: period }) }}
          </p>
          <div>
            <button
              type="button"
              class="py-2.5 px-3 rounded-md bg-primary-600 text-text-primary text-xs font-medium hover:bg-primary-500 duration-300"
              :disabled="requesting"
              @click="requestFeedback"
            >
              {{ $t('coach.profile_feedback_card.get_feedback') }}
            </button>
          </div>
        </template>

        <template v-else-if="state === 'empty'">
          <p class="text-text-secondary text-sm">{{ $t('coach.profile_feedback_card.empty', { map: mapText }) }}</p>
          <div v-if="period < DEFAULT_PROFILE_PERIOD">
            <button
              type="button"
              class="py-2.5 px-3 rounded-md bg-gray-800 text-text-primary text-xs hover:bg-gray-700 duration-300"
              @click="period = DEFAULT_PROFILE_PERIOD"
            >
              {{ $t('coach.profile_feedback_card.show_30_days') }}
            </button>
          </div>
        </template>

        <template v-else>
          <p class="text-text-secondary text-sm">{{ $t('coach.profile_feedback_card.error') }}</p>
          <div>
            <button
              type="button"
              class="py-2.5 px-3 rounded-md bg-gray-800 text-text-primary text-xs hover:bg-gray-700 duration-300"
              :disabled="requesting"
              @click="requestFeedback"
            >
              {{ $t('coach.profile_feedback_card.try_again') }}
            </button>
          </div>
        </template>
      </div>
    </section>
    <CoachLeadNumber
      v-if="showLead"
      :lead="state === 'ready' ? result.lead : null"
      :periodDays="period"
      :mapLabel="mapText"
    />
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import Pill from '../../Pill.vue';
import Info from '../../General/Info.vue';
import AnnotatedAdvice from '../AnnotatedAdvice.vue';
import FeedbackThumbs from '../FeedbackThumbs.vue';
import CoachLeadNumber from './CoachLeadNumber.vue';
import {
  FEEDBACK_ACCESS_LEVEL, PROFILE_PERIODS, DEFAULT_PROFILE_PERIOD, REQUEST_WAIT_MS,
  profileAfterDate, profileResultState, splitFeedback, parseAnnotatedFeedback,
  relativeTime,
} from '../../../utils/coach_feedback';

const props = defineProps({
  accessLevel: { type: Number, default: 0 },
  // The page's Map filter: { name, friendly_name } or null for all maps
  map: { type: Object, default: null },
  // Platform or match type is filtered on the page, which this feedback ignores
  narrowedByOtherFilters: { type: Boolean, default: false },
});

const { t, locale } = useI18n();
const cable = getCurrentInstance()?.appContext.config.globalProperties.$cable;

const period = ref(DEFAULT_PROFILE_PERIOD);
const state = ref(props.accessLevel < FEEDBACK_ACCESS_LEVEL ? 'locked' : 'loading');
const result = ref(null);
// Bumped to re-read the cache when a requested result never announced itself.
const reloadTick = ref(0);
// The POST in flight ({ key, token }): only a repeat of its filter set is
// dropped, so a request for other filters never swallows the one on screen.
// The token lets only the request that set the slot clear it.
const inFlight = ref(null);
// Filter sets already auto-requested this visit: a lost job must fall back to
// the button, not re-request on every fallback re-read.
const autoRequested = new Set();
let waitTimer = null;
let subscription = null;
let consumer = null;

const mapName = computed(() => props.map?.name || null);
const after = computed(() => profileAfterDate(period.value));
const mapText = computed(() => props.map?.friendly_name || props.map?.name || t('coach.profile_feedback_card.all_maps'));
const sinceText = computed(() => new Date(`${after.value}T00:00:00Z`)
  .toLocaleDateString(locale.value, { day: 'numeric', month: 'short', timeZone: 'UTC' }));
const updatedText = computed(() => {
  const at = result.value?.generated_at && new Date(result.value.generated_at);
  return at && !Number.isNaN(at.getTime()) ? relativeTime(at, locale.value) : null;
});
const split = computed(() => splitFeedback(result.value?.feedback_text));
const annotated = computed(() => parseAnnotatedFeedback(result.value?.feedback_text));
// The real box only for a lead the card has a label for. While loading, a
// skeleton holds its place only if the last result had one, so a viewer whose
// results carry no lead never sees a box appear and vanish.
const hasLead = (data) => {
  const lead = data?.lead;
  // impact-score-model sends man-advantage states only; anything else is hidden.
  return lead?.kind === 'state'
    ? lead.hold != null && lead.if_die != null && lead.allies > lead.enemies
    : !!lead?.metric && lead.player != null && lead.baseline != null;
};
const hadLead = ref(false);
watch(result, (data) => { hadLead.value = hasLead(data); });
const showLead = computed(() => (state.value === 'ready'
  ? hasLead(result.value)
  : hadLead.value && (state.value === 'loading' || state.value === 'writing')));
const isDefaultView = computed(() => !mapName.value && period.value === DEFAULT_PROFILE_PERIOD);
const filterParams = () => ({ map_name: mapName.value || undefined, after: after.value });
// A reply for filters the viewer has since changed must not land on screen.
const filterKey = (params) => `${params.map_name || ''}|${params.after}`;
const isCurrent = (params) => filterKey(params) === filterKey(filterParams());
const requesting = computed(() => inFlight.value?.key === filterKey(filterParams()));

function stopWaiting() {
  clearTimeout(waitTimer);
  waitTimer = null;
}

function showResult(data) {
  stopWaiting();
  result.value = data;
  state.value = profileResultState(data);
}

// The job waits up to 90s on the worker; if no cable event arrives by then
// (socket down, job lost), read whatever the cache holds. A miss then shows
// the button: load() auto-requests a filter set only once per visit.
function waitForResult() {
  state.value = 'writing';
  stopWaiting();
  waitTimer = setTimeout(() => { reloadTick.value += 1; }, REQUEST_WAIT_MS);
}

async function requestFeedback() {
  const params = filterParams();
  const key = filterKey(params);
  if (inFlight.value?.key === key) return;
  const token = {};
  inFlight.value = { key, token };
  try {
    const response = await axios.post('/coach/profile_feedback', params);
    if (!isCurrent(params)) return;
    if (response.status === 200) showResult(response.data);
    else waitForResult();
  } catch (error) {
    if (!isCurrent(params)) return;
    const status = error?.response?.status;
    if (status === 409) waitForResult();
    else if (status === 403) state.value = 'locked';
    else state.value = 'error';
  } finally {
    if (inFlight.value?.token === token) inFlight.value = null;
  }
}

// Only the default view generates on load; every other combination is one LLM
// call per click, cached server-side for 6 hours.
async function load() {
  if (state.value === 'locked') return;
  stopWaiting();
  state.value = 'loading';
  const params = filterParams();
  try {
    const { data } = await axios.get('/coach/profile_feedback', { params });
    if (isCurrent(params)) showResult(data);
  } catch (error) {
    if (!isCurrent(params)) return;
    if (error?.response?.status !== 404) {
      state.value = 'error';
    } else if (isDefaultView.value && !autoRequested.has(params.after)) {
      autoRequested.add(params.after);
      await requestFeedback();
    } else {
      state.value = 'idle';
    }
  }
}

// CoachChannel is per user and carries no text, only which filters are
// readable; ignore events for other filters than the ones on screen.
function subscribe() {
  if (!cable) return;
  consumer = cable.connect();
  subscription = consumer.subscriptions.create({ channel: 'CoachChannel' }, {
    received: (data) => {
      if (data?.type !== 'profile_feedback_ready') return;
      if ((data.map_name || null) !== mapName.value || data.after !== after.value) return;
      load();
    },
  });
}

watch([period, mapName, reloadTick], load);

onMounted(() => {
  if (state.value === 'locked') return;
  subscribe();
  load();
});

onBeforeUnmount(() => {
  stopWaiting();
  subscription?.unsubscribe();
  consumer?.disconnect();
});
</script>
